import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/providers/AuthProvider';
import type { Database } from '@/types/database';
import type { Ticket, TicketStatus } from './types';

type TicketUpdate = Database['public']['Tables']['tickets']['Update'];

export function useAllTickets() {
  return useQuery({
    queryKey: ['tickets', 'all'],
    staleTime: 1000 * 30,
    queryFn: async (): Promise<Ticket[]> => {
      const { data, error } = await supabase
        .from('tickets')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as Ticket[];
    },
  });
}

export function useMyTickets() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['tickets', 'mine', user?.id],
    enabled: !!user?.id,
    staleTime: 1000 * 30,
    queryFn: async (): Promise<Ticket[]> => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('tickets')
        .select('*')
        .eq('reporter_user_id', user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as Ticket[];
    },
  });
}

export interface CreateTicketInput {
  title: string;
  description: string;
  file?: File;
  clientId?: string | null;
}

export function useCreateTicket() {
  const qc = useQueryClient();
  const { user, profile } = useAuth();

  return useMutation({
    mutationFn: async (input: CreateTicketInput): Promise<Ticket> => {
      if (!user?.id || !profile) throw new Error('Não autenticado');

      // 1. Insere ticket sem anexo
      const insertPayload = {
        reporter_user_id: user.id,
        reporter_name: profile.name || user.email || 'Anônimo',
        reporter_email: user.email ?? null,
        reporter_role: profile.role,
        title: input.title.trim(),
        description: input.description.trim(),
        client_id: input.clientId ?? null,
      };

      const { data: ticket, error: insertErr } = await supabase
        .from('tickets')
        .insert(insertPayload)
        .select()
        .single();

      if (insertErr) throw insertErr;
      const created = ticket as Ticket;

      // 2. Se houver arquivo, sobe e atualiza
      if (input.file) {
        const sanitized = input.file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `${created.id}/${Date.now()}_${sanitized}`;

        const { error: uploadErr } = await supabase.storage
          .from('ticket-attachments')
          .upload(path, input.file, {
            contentType: input.file.type,
            upsert: false,
          });

        if (uploadErr) {
          // Ticket já criado; só logamos o erro de upload
          throw new Error(`Ticket criado, mas anexo falhou: ${uploadErr.message}`);
        }

        const { data: updated, error: updateErr } = await supabase
          .from('tickets')
          .update({
            attachment_path: path,
            attachment_type: input.file.type,
            attachment_size_bytes: input.file.size,
          })
          .eq('id', created.id)
          .select()
          .single();

        if (updateErr) throw updateErr;
        return updated as Ticket;
      }

      return created;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets'] });
      toast.success('Chamado aberto. Obrigado pelo feedback!');
    },
    onError: (e: Error) => toast.error(`Erro ao abrir chamado: ${e.message}`),
  });
}

export function useUpdateTicketStatus() {
  const qc = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      id,
      status,
      note,
    }: {
      id: string;
      status: TicketStatus;
      note?: string;
    }) => {
      const updates: TicketUpdate = { status };
      if (status === 'resolved') {
        updates.resolved_at = new Date().toISOString();
        updates.resolved_by_user_id = user?.id ?? null;
        if (note !== undefined) updates.resolution_note = note;
      } else {
        updates.resolved_at = null;
        updates.resolved_by_user_id = null;
      }
      const { error } = await supabase.from('tickets').update(updates).eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['tickets'] });
      const labels: Record<TicketStatus, string> = {
        open: 'Reaberto',
        in_progress: 'Em andamento',
        resolved: 'Resolvido',
      };
      toast.success(labels[vars.status]);
    },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });
}

/** Gera signed URL (1h) para o anexo do ticket. Retorna null se sem anexo ou expirado. */
export async function getTicketAttachmentUrl(ticket: Ticket): Promise<string | null> {
  if (!ticket.attachment_path || ticket.attachment_purged) return null;
  const { data, error } = await supabase.storage
    .from('ticket-attachments')
    .createSignedUrl(ticket.attachment_path, 60 * 60);
  if (error) return null;
  return data?.signedUrl ?? null;
}
